// OtpInput.tsx
import * as React from "react";
import { Box, TextField } from "@mui/material";
import { useInput } from "react-admin";

type OtpInputProps = {
  source: string;
  length?: number; // default 8
  uppercase?: boolean; // default true
  onlyAlnum?: boolean; // default true
  disabled?: boolean;
};

export default function OtpInput({
  source,
  length = 8,
  uppercase = true,
  onlyAlnum = true,
  disabled,
}: OtpInputProps) {
  const { field } = useInput({ source }); // binds to RA form value
  const value: string = (field.value ?? "") as string;

  // refs to underlying <input> elements (not wrappers)
  const inputsRef = React.useRef<Array<HTMLInputElement | null>>([]);

  // turn the current value into an array of length `length`
  const chars = React.useMemo(() => {
    const out: string[] = Array.from({ length }, (_, i) => value[i] ?? "");
    return out;
  }, [value, length]);

  const sanitize = (s: string) => {
    let out = s ?? "";
    if (uppercase) out = out.toUpperCase();
    if (onlyAlnum) out = out.replace(/[^A-Z0-9]/gi, "");
    return out;
  };

  // set char at index and update the *form* value (string without padding)
  const setCharAt = (idx: number, ch: string) => {
    const arr = [...chars];
    arr[idx] = ch;
    field.onChange(arr.join("").slice(0, length));
  };

  const handleChange = (idx: number, raw: string) => {
    const s = sanitize(raw).slice(0, 1);
    if (!s) return;
    setCharAt(idx, s);
    // focus next box
    inputsRef.current[idx + 1]?.focus();
  };

  return (
    <Box display="flex" gap={1}>
      {Array.from({ length }).map((_, i) => (
        <TextField
          key={i}
          value={chars[i]}
          onChange={(e) => handleChange(i, e.target.value)}
          inputRef={(el) => (inputsRef.current[i] = el)}
          variant="outlined"
          size="small"
          sx={{ width: 48 }}
          disabled={disabled}
        />
      ))}
    </Box>
  );
}
